import { useState, useEffect, useCallback } from 'react';
import { dayBoundary } from '../utils/tableFilterQuery';
import { storage } from '../utils/storage';

export function applyFilters(data, columns, columnFilters, search, searchFields) {
  const colMap = Object.fromEntries((columns || []).map(c => [c.key, c]));
  let result = data;

  for (const [colKey, filter] of Object.entries(columnFilters)) {
    if (!filter) continue;
    const col = colMap[colKey];
    if (!col) continue;
    result = result.filter(row => matchesFilter(row[colKey], filter, col.type));
  }

  if (search && searchFields?.length > 0) {
    const q = search.toLowerCase();
    result = result.filter(row =>
      searchFields.some(f => String(row[f] ?? '').toLowerCase().includes(q))
    );
  }

  return result;
}

function matchesFilter(value, filter, type) {
  if (type === 'text') {
    // New model: {condition, likeMode, not, value}
    if (filter.condition !== undefined) {
      const { not, condition, likeMode, value: v } = filter;
      const str = String(value ?? '').toLowerCase();
      const q   = String(v ?? '').toLowerCase();
      let matches;
      if (condition === 'IS NULL') {
        matches = !value || str.trim() === '';
      } else if (condition === 'LIKE') {
        if (likeMode === 'begins') matches = str.startsWith(q);
        else if (likeMode === 'ends') matches = str.endsWith(q);
        else matches = str.includes(q);
      } else if (condition === '=') {
        matches = str === q;
      } else if (condition === '>') {
        matches = str > q;
      } else if (condition === '<') {
        matches = str < q;
      } else {
        matches = str.includes(q);
      }
      return not ? !matches : matches;
    }
    // Legacy model: {op, value}
    const { op, value: v } = filter;
    const str = String(value ?? '').toLowerCase();
    const q   = String(v ?? '').toLowerCase();
    if (op === 'is_empty')    return !value || str.trim() === '';
    if (op === 'not_empty')   return !!value && str.trim() !== '';
    if (op === 'contains')    return str.includes(q);
    if (op === 'equals')      return str === q;
    if (op === 'starts_with') return str.startsWith(q);
    if (op === 'ends_with')   return str.endsWith(q);
    // Bilinmeyen op: hiçbir satırı eşleştirme — "aktif filtre varken tüm
    // satırların görünmesi" yanıltıcı olur, boş sonuç hatayı görünür kılar.
    return false;
  }
  if (type === 'date') {
    if (!value) return filter.op === 'is_empty';
    const d   = new Date(value);
    const now = new Date();
    const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (filter.op === 'today')    return d >= sod && d < new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    if (filter.op === 'last_7d')  return d >= new Date(now.getTime() - 7  * 86400000) && d <= now;
    if (filter.op === 'last_30d') return d >= new Date(now.getTime() - 30 * 86400000) && d <= now;
    if (filter.op === 'last_90d') return d >= new Date(now.getTime() - 90 * 86400000) && d <= now;
    if (filter.op === 'before')   return filter.value ? d < new Date(dayBoundary(filter.value)) : true;
    if (filter.op === 'after')    return filter.value ? d >= new Date(dayBoundary(filter.value, true)) : true;
    if (filter.op === 'custom') {
      const from = filter.value ? new Date(dayBoundary(filter.value)) : null;
      const to = filter.value2 ? new Date(dayBoundary(filter.value2, true)) : null;
      if (from && d < from) return false;
      if (to && d >= to) return false;
      return true;
    }
    return true;
  }
  if (type === 'number') {
    const { op, value: v, value2 } = filter;
    if (op === 'is_empty')  return value === null || value === undefined || value === '';
    if (op === 'not_empty') return value !== null && value !== undefined && value !== '';
    // Missing values must never satisfy a numeric comparison (otherwise
    // null/blank is coerced to 0 and leaks into eq/lt/between results).
    if (value === null || value === undefined || value === '') return false;
    const n = Number(value);
    if (Number.isNaN(n)) return false;
    if (op === 'eq')      return n === Number(v);
    if (op === 'gt')      return n >  Number(v);
    if (op === 'lt')      return n <  Number(v);
    if (op === 'between') return n >= Number(v) && n <= Number(value2);
    return true;
  }
  if (type === 'select') {
    const { values } = filter;
    if (!values || values.length === 0) return true;
    return values.includes(String(value ?? ''));
  }
  return true;
}

export function useDataTable(storageKey, defaults = {}) {
  const lsKey = storageKey ? `dr.table.${storageKey}` : null;

  const [sort, _setSort] = useState(() => {
    if (!lsKey) return null;
    const saved = storage.getJSON(lsKey) || {};
    if (saved.columnFilters !== undefined) {
      const { columnFilters: _drop, ...rest } = saved;
      storage.setJSON(lsKey, rest);
    }
    return saved.sort ?? null;
  });
  const [columnFilters, _setColumnFilters] = useState({});
  const [pageSize, _setPageSize] = useState(() => {
    if (!lsKey) return defaults.pageSize || 25;
    const saved = storage.getJSON(lsKey) || {};
    return saved.pageSize ?? (defaults.pageSize || 25);
  });
  const [page,    setPage]    = useState(1);
  const [search,  _setSearch] = useState('');

  useEffect(() => {
    if (!lsKey) return;
    storage.setJSON(lsKey, { sort, pageSize });
  }, [lsKey, sort, pageSize]);

  const setSort = useCallback((col) => {
    _setColumnFilters(prev => Object.fromEntries(Object.entries(prev).map(([key, filter]) => {
      if (!filter?.order) return [key, filter];
      const { order, ...condition } = filter;
      return [key, condition];
    })));
    _setSort(prev =>
      prev?.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' }
    );
    setPage(1);
  }, []);

  const setColumnFilter = useCallback((colKey, value) => {
    if (value?.order) _setSort({ col: colKey, dir: value.order });
    _setColumnFilters(prev => {
      const next = Object.fromEntries(Object.entries(prev).map(([key, filter]) => {
        if (!value?.order || !filter?.order) return [key, filter];
        const { order, ...condition } = filter;
        return [key, condition];
      }));
      return { ...next, [colKey]: value };
    });
    setPage(1);
  }, []);

  const clearColumnFilter = useCallback((colKey) => {
    _setColumnFilters(prev => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
    setPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    _setColumnFilters({});
    setPage(1);
  }, []);

  const setPageSize = useCallback((n) => {
    _setPageSize(n);
    setPage(1);
  }, []);

  const activeFilterCount = Object.values(columnFilters).filter(Boolean).length;

  const setSearch = useCallback((q) => {
    _setSearch(q);
    setPage(1);
  }, []);

  const restoreView = useCallback((view) => {
    _setColumnFilters(view.columnFilters || {});
    _setSearch(view.search || '');
    _setSort(view.sort || null);
    setPage(1);
  }, []);

  return {
    sort, setSort, restoreView,
    search, setSearch,
    columnFilters, setColumnFilter, clearColumnFilter, clearAllFilters, activeFilterCount,
    page, setPage,
    pageSize, setPageSize,
  };
}
